import { useState, useEffect } from 'react';
import { Unsubscribe } from 'firebase/firestore';
import { isFirestoreAssertionError } from '../utils/firestore-errors';

/**
 * Generic hook for subscribing to Firestore collections with real-time updates
 * Provides loading, error, and data states with automatic cleanup
 */
export function useFirestoreCollection<T>(
  subscribe: (
    onUpdate: (data: T[]) => void,
    onError?: (error: Error) => void
  ) => Unsubscribe,
  deps: React.DependencyList = []
) {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    let unsubscribe: (() => void) | undefined;
    try {
      unsubscribe = subscribe(
        (newData) => {
          setData(newData);
          setLoading(false);
        },
        (err) => {
          // Firestore emulator can throw assertion errors after restarts —
          // treat as empty data rather than crashing the page
          if (isFirestoreAssertionError(err)) {
            setData([]);
            setLoading(false);
            return;
          }
          setError(err);
          setLoading(false);
        }
      );
    } catch {
      // Catch synchronous errors from Firestore (assertion failures)
      setData([]);
      setLoading(false);
    }

    return () => {
      unsubscribe?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, loading, error };
}
